/**
 * Embedded API Schemas
 *
 * Schemas for Bitrix24 embedded app authentication and sessions.
 */

import { z } from 'zod/v4';
import { proTrialViewSchema } from './admin';
import { consentLegalBasisSchema, consentModeSchema } from './organizations';
import { weekStartsOnSchema } from './settings';

// ============================================================================
// Auth Schemas
// ============================================================================

/**
 * Auth response after Bitrix24 signature verification
 *
 * Returned after successful authentication from embedded Bitrix24 app.
 *
 * @property success - Always true if authentication succeeded
 * @property redirectUrl - URL to redirect user to after auth (dashboard or onboarding)
 * @property expiresAt - ISO 8601 timestamp when session expires
 */
export const embeddedAuthResponseSchema = z.object({
  success: z.boolean(),
  redirectUrl: z.string(),
  expiresAt: z.string(),
});

export const embeddedBootstrapRequestSchema = z
  .object({
    bootstrapToken: z.string().uuid(),
  })
  .strict();

export const embeddedBootstrapResponseSchema = z.object({
  success: z.literal(true),
  /**
   * Uspacy only (#746, spec `2026-10-29-uspacy-card-tab-session.md` §5): every
   * portal of a Uspacy domain is one browser site, so a cookie would be shared
   * across portals. The frame keeps this token in memory and sends it as
   * `Authorization: Bearer`; Bitrix24 gets the cookie and neither field.
   */
  sessionToken: z.string().uuid().optional(),
  /** Uspacy only: the portal origin the frame posts to and accepts messages from. */
  crmOrigin: z.string().url().optional(),
});

/**
 * The flags the embedded Bitrix24 shell reads from the session (#1347) — an
 * ALLOWLIST, not the organisation's flag map. The iframe has no
 * `/feature-flags` to ask (`FeatureFlagsProvider` is bound to Clerk), so the
 * session resolves the org's flags once and hands over exactly these keys,
 * each with the value the web app's flag map gives it: a feature sold from a
 * tier up carries its tier verdict, the rest the resolved flag. The shell
 * feeds them into `FeatureFlagsContext`, and a screen reads
 * `useFeatureFlagsSafe().isEnabled(key)` the same way in both shells.
 *
 * Only what an embedded screen actually reads goes here — the dashboard's
 * quality block (closed preview, #1235), the «Критерии» tab (#1347), the
 * customer-questions blocks of «Голос клиента» (#843, #844, #1605; the
 * section's own tier gate stays `features.voiceOfCustomer`) and the unified
 * dashboard's shell, whose six tabs replace the Overview (closed preview,
 * #1337), the Themes panel of «Голос клиента» (beta, Pro and up, #845) and the
 * coaching blocks of that shell — «Коучинг» of the team, the supervisor's notes
 * and the employee's own coaching (owner decision 19.10, #2058). The
 * deal-state card of the CRM card tab (#745) is not here: the server's 403
 * code tells the tab the tier from the switched-off flag, a session copy could not.
 * Every key added exposes that flag to every embedded user and switches the
 * screens that read it on inside the iframe; internal/ops flags never belong
 * here. Each value is optional with a `false` default, so an edge-api older
 * than a key (or rolled back past it) reads as "off", and an unknown key the
 * server sends is dropped by the parse.
 */
export const embeddedSessionFlagsSchema = z.object({
  enableDashboardQualitySlice: z.boolean().optional().default(false),
  enableCallCriteria: z.boolean().optional().default(false),
  enableCustomerQuestions: z.boolean().optional().default(false),
  enableUnifiedDashboard: z.boolean().optional().default(false),
  enableConversationThemes: z.boolean().optional().default(false),
  enableCoaching: z.boolean().optional().default(false),
});

/** The allowlist by name — what the session resolves and nothing more. */
export const EMBEDDED_SESSION_FLAG_KEYS = embeddedSessionFlagsSchema.keyof().options;

export type EmbeddedSessionFlagKey = (typeof EMBEDDED_SESSION_FLAG_KEYS)[number];
export type EmbeddedSessionFlags = z.infer<typeof embeddedSessionFlagsSchema>;

/**
 * The flags of screens a unified-dashboard card links to, that open in the
 * portal and not in the iframe (#1859): alerts, human review, the
 * analytics reports and the script-steps report. They are NOT in
 * `embeddedSessionFlagsSchema` — that allowlist is the flags that switch
 * screens on inside the iframe, and the catalog backs it with a
 * `bitrix24_embedded` surface. The session reports only which of these are
 * switched off (`screenFlagsOff`), so a card can drop a link whose screen
 * would answer «feature not available».
 */
export const EMBEDDED_SCREEN_FLAG_KEYS = [
  'enableAnalytics',
  'enableAlerts',
  'enableScripts',
  'enableHumanReview',
] as const;

export type EmbeddedScreenFlagKey = (typeof EMBEDDED_SCREEN_FLAG_KEYS)[number];

/**
 * Session response - user session data
 *
 * Contains active session information for embedded app user.
 *
 * @property userId - AICA user ID (linked to Bitrix24 member)
 * @property crmType - CRM platform type (e.g., 'bitrix24')
 * @property crmDomain - CRM instance domain (e.g., 'company.bitrix24.com')
 * @property orgId - AICA organization ID
 * @property expiresAt - ISO 8601 timestamp when session expires
 */
export const embeddedSessionResponseSchema = z.object({
  userId: z.string().nullable(),
  userEmail: z.string().email().nullable(),
  crmType: z.string(),
  crmDomain: z.string(),
  orgId: z.string(),
  role: z.string().nullable(),
  csrfToken: z.string(),
  consentRequired: z.boolean(),
  pendingConsent: z.boolean(),
  onboardingSurveyCompleted: z.boolean(),
  /**
   * Подтвердил ли владелец организации compliance (orgs.settings_json →
   * consentConfig.consentConfirmedBy). Пока false, ingest отбрасывает все
   * записи организации (IngestWorker: reason consent_not_confirmed).
   *
   * Необязательное с дефолтом, а не обязательное: EmbeddedOrgContext парсит
   * ответ через safeParse и на несовпадении бросает — то есть в iframe не
   * рендерится ВСЁ embedded-приложение, а не только гейт. При перекосе
   * деплоя (ui-worker выехал раньше edge-api или edge-api откатили) это
   * положило бы приложение у всех организаций сразу, а перекос стейджинга в
   * этом репозитории — известная проблема. Дефолт true повторяет принятый
   * в ветке fail-open: при неизвестном статусе не гейтим, серверный гейт в
   * ingest всё равно на месте.
   */
  complianceConfirmed: z.boolean().optional().default(true),
  /**
   * Первый день недели организации (#864), уже приведённый
   * (`readWeekStartsOn`). Дашборды iframe режут по нему неделю — для всех
   * ролей, а `GET /settings` есть только у client/admin. Отдаётся участнику
   * организации; сессии, ждущей согласия (`pendingConsent`), — нет.
   *
   * Необязательное без дефолта: отсутствие — «не знаем», и дашборд держит
   * понедельник; перекос деплоя не должен класть iframe (см. выше).
   */
  weekStartsOn: weekStartsOnSchema.optional(),
  /**
   * Что доступно этой организации в embedded-шелле (#765, §5.3). Флаги в
   * iframe не читаются (`FeatureFlagsProvider` завязан на Clerk), поэтому
   * гейт тарифа/флага ассистента серверный, а его итог едет здесь — кнопка
   * ассистента показывается только при `features.assistant`.
   *
   * Optional с дефолтом — по той же причине, что и `complianceConfirmed`
   * выше: перекос деплоя ui-worker/edge-api не должен класть приложение.
   * Дефолт `false` — кнопки нет, пока сервер не сказал обратное.
   */
  //
  // `voiceOfCustomer` — раздел «Голос клиента» (#848, Pro и выше), тот же
  // серверный шлюз тарифа и флага, что у `/analytics/voice`. Optional с
  // дефолтом false: edge-api старше этого поля его не присылает.
  features: z
    .object({
      assistant: z.boolean(),
      /**
       * @deprecated Read `flags.enableDashboardQualitySlice`. Kept one release:
       * edge-api deploys before ui-worker, and a ui-worker built before #1577
       * reads this field — without it the quality block (#1235) would vanish
       * in the rollout window. Remove: #1590.
       */
      dashboardQualitySlice: z.boolean().optional().default(false),
      voiceOfCustomer: z.boolean().optional().default(false),
      /**
       * The session could not ask the Voice of Customer gate (a D1 error):
       * `voiceOfCustomer` is false then, but it is not a "no" — the screen
       * offers to ask again rather than show the plan lock (code-review #1208).
       */
      voiceOfCustomerUnchecked: z.boolean().optional().default(false),
    })
    .optional()
    .default({
      assistant: false,
      dashboardQualitySlice: false,
      voiceOfCustomer: false,
      voiceOfCustomerUnchecked: false,
    }),
  /**
   * The resolved flag subset (`embeddedSessionFlagsSchema`, #1347). Absent —
   * an edge-api older than the field: every flag off, as on a failed read.
   */
  flags: embeddedSessionFlagsSchema.optional().default({
    enableDashboardQualitySlice: false,
    enableCallCriteria: false,
    enableCustomerQuestions: false,
    enableUnifiedDashboard: false,
    enableConversationThemes: false,
    enableCoaching: false,
  }),
  /**
   * Why a tier-gated feature is open or closed (#1897), in the format
   * `GET /feature-flags` sends (`featureGateVerdictSchema`, keyed by flag key)
   * — only the tier-gated flags the session decides (`enableAIAssistant`,
   * `enableVoiceOfCustomer`, `enableDashboardSales`, and those of the
   * `flags` allowlist sold from a tier up — customer questions and themes,
   * which the demo of a closed section reads, #2816), each from the same
   * computation as its boolean in `features` or `flags`. `{ enabled: false, reason: 'tier_not_eligible' }` is the plan
   * lock, `feature_disabled` the switch.
   *
   * Entries are `unknown` here on purpose: the client checks each one on its
   * own (`parseGates`, as for the web flag map), so a refusal code that only a
   * newer edge-api knows reads as "no verdict" (`failed`) for THAT feature and
   * never fails the session — edge-api deploys before ui-worker. Optional
   * WITHOUT a default: absent (an edge-api older than the field, the flags
   * could not be read, or a session still awaiting consent — withheld like
   * `weekStartsOn`) is "no verdict", exactly as before the field. A value that
   * is not an object at all is dropped, not an error.
   */
  gates: z.record(z.string(), z.unknown()).optional().catch(undefined),
  /**
   * The screen flags (`EMBEDDED_SCREEN_FLAG_KEYS`, #1859) the organization has
   * switched OFF — a list of the off ones, not a map, so that "not told" and
   * "on" read the same: no link is hidden. Empty where the flags could not be
   * read (a failed read is not a refusal), from an edge-api older than the
   * field, and for a value that is not a list. The names are plain strings on
   * purpose: a key only a newer edge-api knows is ignored by the client, never
   * a failed session (edge-api deploys before ui-worker).
   */
  screenFlagsOff: z.array(z.string()).readonly().catch([]),
  /**
   * The Pro trial to show (#2816), as `GET /feature-flags` sends it
   * (`proTrialViewSchema`). Withheld from a session still awaiting consent, like
   * `gates`. A value only a newer edge-api knows reads as absent — nothing
   * shown — and never fails the session (edge-api deploys before ui-worker).
   */
  proTrial: proTrialViewSchema.nullable().optional().catch(undefined),
  pendingUserName: z.string().nullable().optional(),
  createdAt: z.string().nullable(),
  expiresAt: z.string(),
});

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Embedded app authentication request
 *
 * Validates Bitrix24 signature and creates embedded session.
 *
 * @property signature - Bitrix24 HMAC signature for request validation
 * @property memberId - Bitrix24 member ID (user identifier in B24)
 * @property domain - Bitrix24 portal domain (e.g., 'company.bitrix24.com')
 * @property timestamp - Unix timestamp when signature was generated
 */
export const embeddedAuthRequestSchema = z
  .object({
    signature: z.string(),
    memberId: z.string(),
    domain: z.string(),
    timestamp: z.string(),
  })
  .strict();

/**
 * Get embedded session request.
 * Identity is supplied exclusively by the HttpOnly session cookie.
 */
export const embeddedSessionRequestSchema = z.object({}).strict();

// ============================================================================
// Consent Schemas
// ============================================================================

/**
 * Consent request for embedded GDPR flow
 *
 * Two of these three fields are preconditions of using the product; the third
 * is a free choice, and the split is deliberate — bundling a newsletter opt-in
 * into "accept our terms" is what Bitrix24 Marketplace moderation objected to
 * (2026-08-12).
 *
 * @property acceptedTerms - Must be true (user accepted terms of service)
 * @property acceptedPrivacy - Must be true (user accepted privacy policy)
 * @property acceptedMarketing - Opt-in to the marketing series. Defaults to
 *   false, so a client that omits it means "no consent", never "yes".
 *
 * NO `email` field, deliberately. Collecting a self-typed address here would be
 * two separate holes:
 *
 *  1. Identity. `resolveEmbeddedIdentity` falls back to `getByEmail` and ADOPTS
 *     the row it finds. An address arriving from the form rather than from the
 *     CRM would let a portal user type a colleague's — or a stranger's — address
 *     and be handed that account's session.
 *  2. Delivery. An unverified address in `users.email` sends that org's digests
 *     to whoever actually owns it.
 *
 * Both need e-mail verification (a code to the address) to be safe, which is its
 * own piece of work. Until then the marketing opt-in applies where an address
 * already exists, and a portal user without one simply is not asked.
 */
export const embeddedConsentRequestSchema = z
  .object({
    acceptedTerms: z.literal(true),
    acceptedPrivacy: z.literal(true),
    acceptedMarketing: z.boolean().default(false),
  })
  .strict();

/**
 * Подтверждение compliance из embedded-приложения (Bitrix24).
 *
 * Enum-поля переиспользуют SSOT из consentConfigSchema (organizations.ts),
 * но consentConfirmedBy/At здесь нет — актор берётся из сессии, не из тела.
 */
export const embeddedComplianceRequestSchema = z
  .object({
    consentMode: consentModeSchema,
    jurisdiction: z.string().min(1).max(64),
    legalBasis: consentLegalBasisSchema,
  })
  .strict();

// ============================================================================
// Portal Link Schemas
// ============================================================================

/**
 * Portal link response - ready-to-open SSO bridge URL
 *
 * Returned after successful portal link generation from embedded app.
 * Contains a complete URL with SSO ticket for redirecting to the main portal.
 *
 * @property url - Complete URL with SSO ticket for portal redirect (must be valid URL)
 */
export const portalLinkResponseSchema = z.object({
  url: z.url(),
});

/**
 * Обмен claim-билета на привязку Clerk-аккаунта к портальному пользователю.
 *
 * В теле ТОЛЬКО билет: и организация, и пользователь берутся из самого
 * билета, выпущенного внутри верифицированной embedded-сессии. Любое поле
 * личности здесь означало бы, что привязку можно направить на чужую строку,
 * назвав её в запросе, — `.strict()` отвергает такую попытку целиком.
 */
export const portalClaimRequestSchema = z
  .object({
    token: z.string().min(16).max(512),
  })
  .strict();

/**
 * Итог привязки.
 *
 * `merged` — у пришедшего Clerk-аккаунта уже была строка в Aelo (регистрация
 * на сайте, приглашение, другой портал), и портальная влилась в неё. Поле
 * читают журнал и аудит; экран привязки его сейчас НЕ показывает — человек
 * попадает прямо в дашборд. Обещать здесь объяснение в интерфейсе, которого
 * нет, значит описывать несуществующее поведение.
 */
export const portalClaimResponseSchema = z.object({
  orgId: z.string().min(1),
  userId: z.string().min(1),
  role: z.string().min(1),
  merged: z.boolean(),
});

// ============================================================================
// Type exports
// ============================================================================

export type EmbeddedAuthResponse = z.infer<typeof embeddedAuthResponseSchema>;
export type EmbeddedBootstrapRequest = z.infer<typeof embeddedBootstrapRequestSchema>;
export type EmbeddedBootstrapResponse = z.infer<typeof embeddedBootstrapResponseSchema>;
export type EmbeddedSessionResponse = z.infer<typeof embeddedSessionResponseSchema>;
export type EmbeddedAuthRequest = z.infer<typeof embeddedAuthRequestSchema>;
export type EmbeddedSessionRequest = z.infer<typeof embeddedSessionRequestSchema>;
export type EmbeddedConsentRequest = z.infer<typeof embeddedConsentRequestSchema>;
export type EmbeddedComplianceRequest = z.infer<typeof embeddedComplianceRequestSchema>;
export type PortalLinkResponse = z.infer<typeof portalLinkResponseSchema>;
export type PortalClaimRequest = z.infer<typeof portalClaimRequestSchema>;
export type PortalClaimResponse = z.infer<typeof portalClaimResponseSchema>;
